import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  FileText, Download, Users, GraduationCap, Building2, BookOpen, 
  TrendingUp, Award, Briefcase, BarChart3, PieChart, RefreshCw
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NAACReports() {
  const [activeTab, setActiveTab] = useState('placement');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('token');

  const fetchReport = async (endpoint) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/naac-reports/${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReportData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport('placement-statistics');
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const endpoints = {
      'placement': 'placement-statistics',
      'ratio': 'student-teacher-ratio',
      'pass': 'pass-percentage',
      'enrollment': 'student-enrollment',
      'infrastructure': 'infrastructure-summary',
      'ssr': 'ssr-data-export'
    };
    fetchReport(endpoints[tab]);
  };

  const downloadReport = () => {
    if (!reportData) return;
    const dataStr = JSON.stringify(reportData, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `naac_${activeTab}_report_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const StatCard = ({ title, value, icon: Icon, color = 'blue' }) => (
    <Card className={`border-l-4 border-l-${color}-500`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
          </div>
          <div className={`w-12 h-12 bg-${color}-100 rounded-full flex items-center justify-center`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6 space-y-6" data-testid="naac-reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">NAAC Reports</h1>
          <p className="text-gray-500">Reports required for NAAC accreditation</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={() => handleTabChange(activeTab)}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={downloadReport} disabled={!reportData}>
            <Download className="h-4 w-4 mr-2" />
            Export JSON
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid grid-cols-3 lg:grid-cols-6 gap-2 h-auto p-1">
          <TabsTrigger value="placement" className="flex items-center gap-2 py-2">
            <Briefcase className="h-4 w-4" />
            <span className="hidden sm:inline">Placement</span>
          </TabsTrigger>
          <TabsTrigger value="ratio" className="flex items-center gap-2 py-2">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Student-Teacher</span>
          </TabsTrigger>
          <TabsTrigger value="pass" className="flex items-center gap-2 py-2">
            <Award className="h-4 w-4" />
            <span className="hidden sm:inline">Pass %</span>
          </TabsTrigger>
          <TabsTrigger value="enrollment" className="flex items-center gap-2 py-2">
            <GraduationCap className="h-4 w-4" />
            <span className="hidden sm:inline">Enrollment</span>
          </TabsTrigger>
          <TabsTrigger value="infrastructure" className="flex items-center gap-2 py-2">
            <Building2 className="h-4 w-4" />
            <span className="hidden sm:inline">Infrastructure</span>
          </TabsTrigger>
          <TabsTrigger value="ssr" className="flex items-center gap-2 py-2">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">SSR Export</span>
          </TabsTrigger>
        </TabsList>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : error ? (
          <Card className="mt-6">
            <CardContent className="p-6 text-center text-red-500">
              {error}
            </CardContent>
          </Card>
        ) : reportData ? (
          <>
            {/* Placement Statistics */}
            <TabsContent value="placement" className="mt-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard 
                  title="Eligible Students" 
                  value={reportData.summary?.total_eligible_students || 0}
                  icon={Users}
                  color="blue"
                />
                <StatCard 
                  title="Students Placed" 
                  value={reportData.summary?.students_placed || 0}
                  icon={Award}
                  color="green"
                />
                <StatCard 
                  title="Placement %" 
                  value={`${reportData.summary?.placement_percentage || 0}%`}
                  icon={TrendingUp}
                  color="purple"
                />
                <StatCard 
                  title="Companies Visited" 
                  value={reportData.summary?.total_companies_visited || 0}
                  icon={Building2}
                  color="orange"
                />
              </div>

              {/* Company-wise Table */}
              {reportData.company_wise_placement?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Company-wise Placement</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left">Company Name</th>
                            <th className="px-4 py-3 text-center">Students Placed</th>
                            <th className="px-4 py-3 text-right">Avg Package (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.company_wise_placement.map((company, i) => (
                            <tr key={i} className="border-t">
                              <td className="px-4 py-3">{company.company_name}</td>
                              <td className="px-4 py-3 text-center">{company.students_placed}</td>
                              <td className="px-4 py-3 text-right">
                                {company.avg_package ? `₹${company.avg_package.toLocaleString('en-IN')}` : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Jobs List */}
              {reportData.jobs_list?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Active Jobs ({reportData.jobs_list.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {reportData.jobs_list.slice(0, 5).map((job, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium">{job.title}</p>
                            <p className="text-sm text-gray-500">{job.company_name}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-green-600">
                              ₹{(job.salary_range_min/100000).toFixed(1)}L - ₹{(job.salary_range_max/100000).toFixed(1)}L
                            </p>
                            <p className="text-sm text-gray-500">{job.applications_count} applications</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Student-Teacher Ratio */}
            <TabsContent value="ratio" className="mt-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <StatCard 
                  title="Total Students" 
                  value={reportData.summary?.total_students || 0}
                  icon={Users}
                  color="blue"
                />
                <StatCard 
                  title="Teaching Staff" 
                  value={reportData.summary?.total_teaching_staff || 0}
                  icon={GraduationCap}
                  color="green"
                />
                <StatCard 
                  title="Overall Ratio" 
                  value={reportData.summary?.overall_ratio || 'N/A'}
                  icon={BarChart3}
                  color="purple"
                />
                <StatCard 
                  title="Programs" 
                  value={reportData.summary?.total_programs || 0}
                  icon={BookOpen}
                  color="orange"
                />
              </div>

              {/* Program-wise */}
              {reportData.program_wise?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Program-wise Ratio</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left">Program</th>
                            <th className="px-4 py-3 text-center">Students</th>
                            <th className="px-4 py-3 text-center">Teachers</th>
                            <th className="px-4 py-3 text-center">Ratio</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.program_wise.map((p, i) => (
                            <tr key={i} className="border-t">
                              <td className="px-4 py-3">{p.program_name}</td>
                              <td className="px-4 py-3 text-center">{p.student_count}</td>
                              <td className="px-4 py-3 text-center">{p.teacher_count}</td>
                              <td className="px-4 py-3 text-center font-medium">{p.ratio}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Pass Percentage */}
            <TabsContent value="pass" className="mt-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <StatCard 
                  title="Total Exams" 
                  value={reportData.summary?.total_exams || 0}
                  icon={FileText}
                  color="blue"
                />
                <StatCard 
                  title="Students Appeared" 
                  value={reportData.summary?.total_students_appeared || 0}
                  icon={Users}
                  color="green"
                />
                <StatCard 
                  title="Students Passed" 
                  value={reportData.summary?.total_students_passed || 0}
                  icon={Award}
                  color="purple"
                />
                <StatCard 
                  title="Overall Pass %" 
                  value={`${reportData.summary?.overall_pass_percentage || 0}%`}
                  icon={TrendingUp}
                  color="orange"
                />
              </div>

              {/* Exam-wise Results */}
              {reportData.exam_wise_results?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Exam-wise Results</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left">Exam Name</th>
                            <th className="px-4 py-3 text-center">Appeared</th>
                            <th className="px-4 py-3 text-center">Passed</th>
                            <th className="px-4 py-3 text-center">Failed</th>
                            <th className="px-4 py-3 text-center">Pass %</th>
                            <th className="px-4 py-3 text-center">Avg Marks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.exam_wise_results.map((exam, i) => (
                            <tr key={i} className="border-t">
                              <td className="px-4 py-3">{exam.exam_name}</td>
                              <td className="px-4 py-3 text-center">{exam.total_appeared}</td>
                              <td className="px-4 py-3 text-center text-green-600">{exam.total_passed}</td>
                              <td className="px-4 py-3 text-center text-red-600">{exam.total_failed}</td>
                              <td className="px-4 py-3 text-center font-medium">{exam.pass_percentage}%</td>
                              <td className="px-4 py-3 text-center">{exam.average_marks}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {reportData.exam_wise_results?.length === 0 && (
                <Card>
                  <CardContent className="p-6 text-center text-gray-500">
                    No examination data available. Add exams and marks to generate this report.
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Student Enrollment */}
            <TabsContent value="enrollment" className="mt-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <StatCard 
                  title="Total Students" 
                  value={reportData.summary?.total_students || 0}
                  icon={Users}
                  color="blue"
                />
                <StatCard 
                  title="Male Students" 
                  value={reportData.summary?.male_students || 0}
                  icon={Users}
                  color="green"
                />
                <StatCard 
                  title="Female Students" 
                  value={reportData.summary?.female_students || 0}
                  icon={Users}
                  color="pink"
                />
                <StatCard 
                  title="Programs" 
                  value={reportData.summary?.total_programs || 0}
                  icon={BookOpen}
                  color="purple"
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Category-wise */}
                <Card>
                  <CardHeader>
                    <CardTitle>Category-wise Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(reportData.category_wise || {}).map(([category, count]) => (
                        <div key={category} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="font-medium">{category}</span>
                          <span className="text-lg font-bold">{count}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Program-wise */}
                <Card>
                  <CardHeader>
                    <CardTitle>Program-wise Enrollment</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {reportData.program_wise?.map((p, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="font-medium">{p.program_name}</span>
                          <div className="text-right">
                            <span className="text-lg font-bold">{p.total_students}</span>
                            <span className="text-sm text-gray-500 ml-2">
                              (M:{p.male}, F:{p.female})
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Infrastructure */}
            <TabsContent value="infrastructure" className="mt-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Library */}
                <Card>
                  <CardHeader className="bg-blue-50">
                    <CardTitle className="flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-blue-600" />
                      Library
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Total Books</span>
                      <span className="font-bold">{reportData.library?.total_books || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Books Issued</span>
                      <span className="font-bold">{reportData.library?.total_issues || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Members</span>
                      <span className="font-bold">{reportData.library?.registered_members || 0}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Hostel */}
                <Card>
                  <CardHeader className="bg-green-50">
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-green-600" />
                      Hostel
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Total Boarders</span>
                      <span className="font-bold">{reportData.hostel?.total_boarders || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Capacity</span>
                      <span className="font-bold">{reportData.hostel?.capacity || 'N/A'}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Transport */}
                <Card>
                  <CardHeader className="bg-orange-50">
                    <CardTitle className="flex items-center gap-2">
                      <Briefcase className="h-5 w-5 text-orange-600" />
                      Transport
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Vehicles</span>
                      <span className="font-bold">{reportData.transport?.total_vehicles || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Routes</span>
                      <span className="font-bold">{reportData.transport?.total_routes || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Students Using</span>
                      <span className="font-bold">{reportData.transport?.students_using_transport || 0}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* SSR Export */}
            <TabsContent value="ssr" className="mt-6 space-y-6">
              <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold mb-2">NAAC Self Study Report (SSR) Data</h2>
                  <p className="text-blue-100 mb-4">
                    This export contains all available data for NAAC SSR preparation. 
                    Download and use this as a starting point for your SSR document.
                  </p>
                  <Button variant="secondary" onClick={downloadReport}>
                    <Download className="h-4 w-4 mr-2" />
                    Download Complete SSR Data
                  </Button>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Data Available */}
                <Card>
                  <CardHeader className="bg-green-50">
                    <CardTitle className="text-green-700">✅ Data Available in System</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <ul className="space-y-2">
                      {reportData.data_availability?.available?.map((item, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                {/* Needs Manual Entry */}
                <Card>
                  <CardHeader className="bg-yellow-50">
                    <CardTitle className="text-yellow-700">⚠️ Needs Manual Entry</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <ul className="space-y-2">
                      {reportData.data_availability?.needs_manual_entry?.map((item, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>

              {/* Criterion Summary */}
              <Card>
                <CardHeader>
                  <CardTitle>NAAC Criterion-wise Summary</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[
                      { num: 1, title: 'Curricular Aspects', data: reportData.criterion_1_curricular_aspects },
                      { num: 2, title: 'Teaching-Learning', data: reportData.criterion_2_teaching_learning },
                      { num: 3, title: 'Research', data: reportData.criterion_3_research },
                      { num: 4, title: 'Infrastructure', data: reportData.criterion_4_infrastructure },
                      { num: 5, title: 'Student Support', data: reportData.criterion_5_student_support },
                      { num: 6, title: 'Governance', data: reportData.criterion_6_governance },
                    ].map((c) => (
                      <div key={c.num} className="p-4 border rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                            {c.num}
                          </span>
                          <span className="font-medium">{c.title}</span>
                        </div>
                        <div className="text-sm text-gray-500">
                          {c.data && Object.entries(c.data).filter(([k]) => k !== 'note').map(([key, value]) => (
                            <div key={key} className="flex justify-between py-1">
                              <span>{key.replace(/_/g, ' ')}</span>
                              <span className="font-medium">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </>
        ) : null}
      </Tabs>
    </div>
  );
}
